import { useApolloClient } from '@apollo/client';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { VERIFY_EMAIL_MUTATION, RESEND_VERIFICATION_EMAIL_MUTATION } from './auth.mutations';
import Logo from '../../Treston_logo.svg';
import Spinner from '../Spinner.component';

interface VerifyEmailFormProps {
  token: string;
  onSwitchToLogin: () => void;
}

const VerifyEmailForm: React.FC<VerifyEmailFormProps> = ({ token, onSwitchToLogin }) => {
  const apolloClient = useApolloClient();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setIsLoading(false);
      setError(t('auth.invalidVerificationToken'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verifyEmail = async () => {
    try {
      await apolloClient.mutate({
        mutation: VERIFY_EMAIL_MUTATION,
        variables: { token },
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || t('auth.emailVerificationFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setResendLoading(true);
    setError('');

    try {
      await apolloClient.mutate({
        mutation: RESEND_VERIFICATION_EMAIL_MUTATION,
        variables: { email },
      });
      setResendSuccess(true);
    } catch (err: any) {
      setError(err.message || t('auth.resendVerificationFailed'));
    } finally {
      setResendLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary-grey-200 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <img className="h-16 w-auto" src={Logo} alt="TRESTON" />
              </div>
              <div className="flex justify-center mb-6">
                <Spinner local />
              </div>
              <p className="text-sm text-primary-grey-300">
                {t('auth.verifyingEmail')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary-grey-200 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <img className="h-16 w-auto" src={Logo} alt="TRESTON" />
              </div>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-6">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-3xl font-semibold text-primary-grey-default">
                {t('auth.emailVerified')}
              </h2>
              <p className="mt-4 text-sm text-primary-grey-300">
                {t('auth.emailVerifiedInstructions')}
              </p>
              <button
                onClick={onSwitchToLogin}
                className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-secondary-orange-default hover:bg-secondary-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-orange-default transition-colors"
              >
                {t('auth.signIn')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (resendSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary-grey-200 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <img className="h-16 w-auto" src={Logo} alt="TRESTON" />
              </div>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-6">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-3xl font-semibold text-primary-grey-default">
                {t('auth.verificationEmailSent')}
              </h2>
              <p className="mt-4 text-sm text-primary-grey-300">
                {t('auth.verificationEmailSentInstructions')}
              </p>
              <button
                onClick={onSwitchToLogin}
                className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-secondary-orange-default hover:bg-secondary-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-orange-default transition-colors"
              >
                {t('auth.backToLogin')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary-grey-200 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <img className="h-16 w-auto" src={Logo} alt="TRESTON" />
            </div>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-6">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-3xl font-semibold text-primary-grey-default">
              {t('auth.emailVerificationFailed')}
            </h2>
            <p className="mt-4 text-sm text-primary-grey-300">
              {error}
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleResendVerification}>
            <div>
              <p className="text-sm text-primary-grey-300 mb-4">
                {t('auth.resendVerificationInstructions')}
              </p>
              <label htmlFor="email" className="block text-sm font-medium text-primary-grey-default mb-2">
                {t('auth.email')}
              </label>
              <input
                id="email"
                type="email"
                required
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-primary-grey-default focus:outline-none focus:ring-2 focus:ring-primary-red-default focus:border-primary-red-default sm:text-sm"
                placeholder={t('auth.emailAddress')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={resendLoading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-secondary-orange-default hover:bg-secondary-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-orange-default disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {resendLoading ? (
                  <Oval
                    color="#ffffff"
                    height={20}
                    width={20}
                    visible={true}
                  />
                ) : (
                  t('auth.resendVerificationEmail')
                )}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={onSwitchToLogin}
                className="text-sm font-medium text-primary-red-default hover:text-primary-red-600 focus:outline-none focus:underline transition-colors"
              >
                {t('auth.backToLogin')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailForm;